// Perplexity-style step timeline at the top of an assistant turn.
// Two modes:
//
//   streaming = true  → expanded, with a spinner on the active row.
//                       The active row's label evolves with elapsed time:
//                       a quiet "(12s)" counter ticks every second, and
//                       at 20s / 60s the copy softens into "Still
//                       working… / Hmm, taking a moment…". The user
//                       always sees the agent is alive — no error
//                       framing while real work is happening.
//   streaming = false → collapsed by default to "Completed N steps  ⌄".
//                       Click to expand and see the post-mortem.
//
// We render the raw step array the WS stream produces, but rewrite the
// dev-log copy ("Identified intent → contact-agent", "Loaded tool
// stats", "Chose 1 tool") into something a recruiter would say out
// loud. Internal-only steps (routing, fast context loads) are dropped
// entirely so the timeline reads as a narration of useful work, not a
// scrollback of the worker logs.

import { type FC, useEffect, useRef, useState } from 'react';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { AlertCircle, ChevronRight } from '@kula-ai/icons-core';
import {
  contextualThinkingLabel,
  toolPastLabel,
  toolProgressLabel,
} from '../../../helpers/toolLabel';
import { StepIcon } from './iconForStep';
import { workingStatusAt } from '../../../helpers/workingStatus';
import { STEP_CONTEXT_LOAD_FAILED } from '../../../constants';

export interface TimelineStep {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'done' | 'error';
  durationMs?: number;
}

interface Props {
  steps: TimelineStep[];
  streaming: boolean;
  /** Approximate tokens streamed down so far (chars/4). Rendered in the
   *  live header as "↓ 4.7k tokens" — the Claude-Code-style signal that
   *  work is flowing even when no new step has started. */
  approxDownTokens?: number;
}

// Elapsed-time thresholds for the active row's copy. The hard numbers
// here are the only place to tune the "feels slow" curve: hold the
// natural label until SOFTEN_AT, hand off to a calmer "still working"
// at SOFTEN_AT, then a gentle nudge ("hmm, this is slow") at WORRY_AT.
// Nothing here is framed as an error — that vocabulary stays out of
// streaming-mode entirely.
const SOFTEN_AT_S = 20;
const WORRY_AT_S = 60;
// Below this, the elapsed counter is hidden because a 1–2 s blink reads
// as noise on a healthy turn.
const SHOW_ELAPSED_AT_S = 4;

// shouldShow filters out steps the user shouldn't see. Internal
// mechanics (which agent picked up the turn, how memories were loaded)
// are noise. Tool calls and slow / failed system steps stay.
const shouldShow = (s: TimelineStep): boolean => {
  if (s.id === 'action:routing') return false;
  // Internal mechanics — context load ("Caught up") and LLM thinking turns
  // ("Decided what to do next") — are agent plumbing, not user-meaningful tool
  // actions. Surface them only while LIVE (the active step drives the
  // streaming header, so the user still sees "Thinking…"/"Catching up…") or on
  // failure; never as a persistent "done" row. Real tool steps always show.
  if (s.id === 'action:context' || s.id.startsWith('action:compose')) {
    return s.status === 'active' || s.status === 'error';
  }
  return true;
};

// rowText is the right label for tool rows: prefer the description
// nexus/kula tools ship ("Searching: ruby developers") over the raw
// tool name ("kula_search"). When no description arrived (dropped frame,
// older backend), humanise the name — a raw "kula_job_ai_scoring_setup"
// token must never reach the timeline. The fallback is TENSE-AWARE:
// finished rows read past tense ("Searched Kula"); only the live row
// keeps the in-flight verb — a done step must never look mid-flight.
const rowText = (s: TimelineStep): string => {
  if (s.description && s.description.trim().length > 0) return s.description;
  if (s.status === 'active') return toolProgressLabel({ name: s.name });
  return toolPastLabel({ name: s.name });
};

// friendlyLabel rewrites internal action labels into human copy. Tool
// steps fall through unchanged — toolProgressLabel already produces
// friendly text for those.
const friendlyLabel = (s: TimelineStep): string => {
  if (s.id === 'action:context') {
    if (s.status === 'active') return 'Catching up on our chat';
    if (s.status === 'error') return STEP_CONTEXT_LOAD_FAILED;
    return 'Caught up';
  }
  if (s.id.startsWith('action:compose')) {
    if (s.status === 'active') return 'Thinking it through';
    if (s.status === 'error') return 'Hit a snag thinking';
    return 'Decided what to do next';
  }
  return rowText(s);
};

// headerText turns the active step into the streaming-header phrase
// ("Searching Kula…"). For action steps we use the friendly label so
// the user never sees "compose:1…".
const headerText = (s: TimelineStep): string => {
  if (s.id.startsWith('action:')) return friendlyLabel(s);
  return toolProgressLabel(s);
};

// softenedLabel folds elapsed-time into the active row's label. The
// returned copy is what's shown both in the row and (with an ellipsis)
// in the header. Bootstrap "Thinking…" follows the same curve — it
// already starts gentle so 0–20s leaves it alone.
const softenedLabel = (
  base: string,
  elapsedSec: number,
  isTool: boolean
): string => {
  if (elapsedSec < SOFTEN_AT_S) return base;
  if (elapsedSec < WORRY_AT_S) {
    // Keep the REAL step label — it's the informative part ("Researching
    // the talent market") — and append the rotating liveness cue. Replacing
    // it with generic verbs made long research steps read as stalled churn.
    return isTool
      ? `${base} — ${workingStatusAt(elapsedSec).toLowerCase()}…`
      : workingStatusAt(elapsedSec);
  }
  return isTool
    ? `${base} — still going; deep research can take a couple of minutes`
    : 'Hmm, this is slow — you can keep waiting or send a new message';
};

const formatTokens = (n: number): string =>
  n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;

const formatElapsed = (sec: number): string => {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
};

const formatDuration = (ms: number): string => {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${ms}ms`;
};

// useElapsedSeconds returns the wall-clock seconds since the given step
// became active. Restarts the counter when the active id changes (so a
// new tool call begins at 0). Stops ticking when there's no active
// step — no interval runs in idle state. The map persists step start
// times across renders so done→active toggles wouldn't reset, though
// the workflow never does that in practice.
const useElapsedSeconds = (activeStepId: string | undefined): number => {
  const startTimesRef = useRef<Map<string, number>>(new Map());
  // `now` only ever changes inside the interval callback (not synchronously
  // in the effect body), keeping clear of react-hooks/set-state-in-effect.
  // Elapsed is derived during render from `now` − the step's start time, so
  // no impure Date.now() runs at render (react-hooks/purity). Starts at 0
  // until the first tick lands; that first sub-second window reads as 0,
  // which is below SHOW_ELAPSED_AT_S anyway.
  const [now, setNow] = useState(0);
  useEffect(() => {
    if (!activeStepId) return;
    if (!startTimesRef.current.has(activeStepId)) {
      startTimesRef.current.set(activeStepId, Date.now());
    }
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [activeStepId]);
  if (!activeStepId || now === 0) return 0;
  const startedAt = startTimesRef.current.get(activeStepId);
  if (!startedAt) return 0;
  return Math.max(0, Math.floor((now - startedAt) / 1000));
};

const StatusGlyph: FC<{ status: TimelineStep['status'] }> = ({ status }) => {
  if (status === 'active') {
    return <Spinner boxSize="3" thickness="1.5px" color="secondary-500" />;
  }
  if (status === 'error') {
    return (
      <Box color="error-500" aria-label="failed">
        <AlertCircle size={14} color="currentColor" />
      </Box>
    );
  }
  return (
    <Box
      as="span"
      w="13px"
      h="13px"
      borderRadius="4px"
      bg="success-500"
      color="white"
      fontSize="10px"
      lineHeight="13px"
      textAlign="center"
      fontWeight={700}
      aria-label="done"
    >
      ✓
    </Box>
  );
};

const Row: FC<{
  step: TimelineStep;
  /** Seconds active so far. Only meaningful when status === 'active'. */
  elapsedSec?: number;
}> = ({ step, elapsedSec }) => {
  const isActive = step.status === 'active';
  const base = friendlyLabel(step);
  const label = isActive
    ? softenedLabel(base, elapsedSec ?? 0, !step.id.startsWith('action:'))
    : base;
  const showElapsed = isActive && (elapsedSec ?? 0) >= SHOW_ELAPSED_AT_S;
  return (
    <Flex align="center" gap="8px" py="2px">
      <Box color="secondary-500" aria-hidden flexShrink={0}>
        <StepIcon step={step} size={14} />
      </Box>
      <Text
        variant="span"
        className={
          step.status === 'error'
            ? 'text-[12px] text-error-700 truncate'
            : isActive
              ? 'text-[12px] font-[550] text-secondary-800 truncate'
              : 'text-[12px] text-secondary-700 truncate'
        }
        title={label}
      >
        {label}
      </Text>
      {showElapsed && (
        <Text
          variant="span"
          className="text-[11px] text-secondary-400 flex-shrink-0"
        >
          {formatElapsed(elapsedSec ?? 0)}
        </Text>
      )}
      {!isActive &&
        typeof step.durationMs === 'number' &&
        step.durationMs > 0 && (
          <Text
            variant="span"
            className="text-[11px] text-secondary-400 flex-shrink-0"
          >
            {formatDuration(step.durationMs)}
          </Text>
        )}
      <Box ml="auto" flexShrink={0}>
        <StatusGlyph status={step.status} />
      </Box>
    </Flex>
  );
};

// dropRetriedErrors hides tool-error rows that don't represent a real
// user-visible failure:
//
//   - During streaming: hide ALL tool errors. Most tool errors in this
//     codebase are smart-tool rejections (incomplete-ranking, etc.)
//     that the LLM will retry within ~2-7s. Showing a "Failed" row
//     mid-stream alarms the user about a transient that fixes itself.
//
//   - Post-stream: keep tool errors only if no later step of the same
//     name succeeded. A succeeded retry → the original was internal
//     recovery and shouldn't surface. No retry → the failure stuck and
//     the user should see it.
//
// Action steps (id prefix `action:`) follow different rules — their
// "error" means routing/context/compose failed for real — so they're
// never filtered here.
const dropRetriedErrors = (
  steps: TimelineStep[],
  streaming: boolean
): TimelineStep[] => {
  const out: TimelineStep[] = [];
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    if (s && s.status === 'error' && !s.id.startsWith('action:')) {
      if (streaming) continue; // assume retry coming
      const supersededByLater = steps
        .slice(i + 1)
        .some((later) => later.name === s.name && later.status !== 'error');
      if (supersededByLater) continue;
    }
    if (s) out.push(s);
  }
  return out;
};

export const StepTimeline: FC<Props> = ({
  steps,
  streaming,
  approxDownTokens,
}) => {
  const visible = dropRetriedErrors(steps.filter(shouldShow), streaming);
  const [expanded, setExpanded] = useState(streaming);

  const active = streaming
    ? visible.find((s) => s.status === 'active')
    : undefined;
  const elapsedSec = useElapsedSeconds(active?.id);

  if (visible.length === 0) return null;

  const errorCount = visible.filter((s) => s.status === 'error').length;
  const total = visible.length;

  const withEllipsis = (s: string) => (s.endsWith('…') ? s : `${s}…`);
  let headerLabel: string;
  let headerExtra: string | undefined;
  if (streaming) {
    if (active) {
      const base = headerText(active);
      const softened = softenedLabel(
        base,
        elapsedSec,
        !active.id.startsWith('action:')
      );
      headerLabel = withEllipsis(softened);
      const extras: string[] = [];
      if (elapsedSec >= SHOW_ELAPSED_AT_S) {
        extras.push(formatElapsed(elapsedSec));
      }
      if ((approxDownTokens ?? 0) > 0) {
        extras.push(`\u2193 ${formatTokens(approxDownTokens ?? 0)} tokens`);
      }
      if (extras.length > 0) headerExtra = extras.join(' \u00b7 ');
    } else {
      headerLabel = withEllipsis(contextualThinkingLabel(visible));
      if ((approxDownTokens ?? 0) > 0) {
        headerExtra = `\u2193 ${formatTokens(approxDownTokens ?? 0)} tokens`;
      }
    }
  } else if (errorCount > 0) {
    headerLabel = `Completed with ${errorCount} issue${errorCount === 1 ? '' : 's'} · ${total} step${total === 1 ? '' : 's'}`;
  } else {
    headerLabel = `Completed ${total} step${total === 1 ? '' : 's'}`;
  }

  // Hide the active row from the body — its content is already in the
  // header. The row reappears as a "done" line once it finishes.
  const rowsToRender = active ? visible.filter((s) => s !== active) : visible;

  return (
    <Box
      borderRadius="8px"
      border="1px solid"
      borderColor="secondary-100"
      bg="base-white"
      px="12px"
      py="8px"
      mb="8px"
      w="100%"
      maxW="520px"
    >
      <Flex
        align="center"
        justify="space-between"
        gap="8px"
        cursor={streaming ? 'default' : 'pointer'}
        onClick={() => {
          if (!streaming) setExpanded((v) => !v);
        }}
        aria-expanded={expanded}
        role={streaming ? undefined : 'button'}
      >
        <Flex align="center" gap="8px" minW={0}>
          {streaming && active && (
            <Spinner boxSize="3" thickness="1.5px" color="primary-500" />
          )}
          <Text
            variant="span"
            className="text-[12px] font-[550] text-secondary-800 truncate"
            title={headerLabel}
          >
            {headerLabel}
          </Text>
          {headerExtra && (
            <Text
              variant="span"
              className="text-[11px] text-secondary-400 flex-shrink-0"
            >
              {headerExtra}
            </Text>
          )}
        </Flex>
        {!streaming && (
          <Box
            color="secondary-500"
            transform={expanded ? 'rotate(90deg)' : 'rotate(0deg)'}
            transition="transform 120ms ease"
            aria-hidden
          >
            <ChevronRight size={14} color="currentColor" />
          </Box>
        )}
      </Flex>
      {expanded && rowsToRender.length > 0 && (
        <Flex direction="column" gap="2px" mt="6px">
          {rowsToRender.map((step) => (
            <Row key={step.id} step={step} />
          ))}
        </Flex>
      )}
    </Box>
  );
};
